// src/components/InvoiceLineItems.js

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  Tooltip,
  Icon,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Delete, Add, Save, WarningAmber } from "@mui/icons-material";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import { useLanguage } from "context/LanguageContext";

function InvoiceLineItems({
  lineItems = [],
  onSave,
  onDirtyChange,
  onItemsChange,
  isSaving,
  discountAmount = 0,
  discountPercentage = 0,
  financialAdjustments = [],
  onAdjustmentsChange,
  netAmount,
}) {
  const { t } = useLanguage();
  const [items, setItems] = useState(lineItems);
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const parseNumber = (value) => {
    if (value === undefined || value === null || value === "") return null;
    const parsed = parseFloat(String(value).replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  };

  const calculateLineAmount = (item) => {
    const quantity = parseNumber(item.quantity) || 0;
    const unitPrice = parseNumber(item.unit_price) || 0;
    return quantity * unitPrice;
  };

  const normalizeItem = (item) => {
    if (item.total_amount !== undefined && item.total_amount !== null && item.total_amount !== "") {
      return item;
    }
    return {
      ...item,
      total_amount: calculateLineAmount(item).toFixed(2),
    };
  };

  // Keep internal state in sync when parent updates lineItems
  useEffect(() => {
    setItems((lineItems || []).map(normalizeItem));
  }, [lineItems]);

  const handleLineItemChange = (index, field, value) => {
    const newItems = [...items];
    const updatedItem = { ...newItems[index], [field]: value };

    if (
      (field === "quantity" || field === "unit_price") &&
      !["settlement", "informational"].includes(updatedItem.line_type)
    ) {
      const quantity = parseNumber(updatedItem.quantity);
      const unitPrice = parseNumber(updatedItem.unit_price);
      if (quantity !== null && unitPrice !== null) {
        updatedItem.total_amount = (quantity * unitPrice).toFixed(2);
      }
    }

    if (
      field === "total_amount" &&
      !["settlement", "informational"].includes(updatedItem.line_type)
    ) {
      const quantity = parseNumber(updatedItem.quantity);
      const totalAmount = parseNumber(updatedItem.total_amount);
      if (quantity !== null && quantity !== 0 && totalAmount !== null) {
        updatedItem.unit_price = (totalAmount / quantity).toFixed(4);
      }
    }

    newItems[index] = updatedItem;
    setItems(newItems);
    onItemsChange?.(newItems);
    onDirtyChange?.();
  };

  const handleDistributeToggle = (index, checked) =>
    handleLineItemChange(index, "line_type", checked ? "distributed" : "item");

  const distributedRowSx = (item) =>
    item.line_type === "distributed"
      ? { backgroundColor: "action.hover", "& input": { fontStyle: "italic" } }
      : {};

  const handleAddLineItem = () => {
    const nextItems = [
      ...items,
      {
        sku: "",
        description: "",
        quantity: 0,
        unit_price: 0,
        total_amount: "0.00",
        vat_percentage: "",
        unit_of_measure: "",
      },
    ];
    setItems(nextItems);
    onItemsChange?.(nextItems);
    onDirtyChange?.();
  };

  const handleRemoveLineItem = (index) => {
    const nextItems = items.filter((_, itemIndex) => itemIndex !== index);
    setItems(nextItems);
    onItemsChange?.(nextItems);
    onDirtyChange?.();
  };

  const handleSave = () => {
    onSave(items);
  };

  const getLineAmount = (item) => {
    const explicitTotal = parseNumber(item.total_amount);
    return explicitTotal !== null ? explicitTotal : calculateLineAmount(item);
  };

  const totalQuantity = items.reduce(
    (acc, item) =>
      ["settlement", "informational"].includes(item.line_type)
        ? acc
        : acc + (parseNumber(item.quantity) || 0),
    0
  );
  const totalAmount = items.reduce(
    (acc, item) =>
      ["settlement", "informational"].includes(item.line_type) ? acc : acc + getLineAmount(item),
    0
  );
  const adjustments = Array.isArray(financialAdjustments) ? financialAdjustments : [];
  const adjustmentsTotal = adjustments.reduce(
    (sum, adjustment) => (adjustment.disabled ? sum : sum + (parseNumber(adjustment.amount) || 0)),
    0
  );
  const handleAdjustmentToggle = (index, enabled) => {
    const nextAdjustments = adjustments.map((adjustment, adjustmentIndex) =>
      adjustmentIndex === index ? { ...adjustment, disabled: !enabled } : adjustment
    );
    onAdjustmentsChange?.(nextAdjustments);
    onDirtyChange?.();
  };
  const adjustedTotal = totalAmount + adjustmentsTotal;

  const parsedNet = parseNumber(netAmount);
  const isLineItemsMismatch =
    parsedNet !== null && items.length > 0 && Math.abs(parsedNet - adjustedTotal) > 0.02;

  const validatedDiscountAmount = parseFloat(discountAmount) || 0;
  const validatedDiscountPercentage = parseFloat(discountPercentage) || 0;
  const indexedItems = items.map((item, index) => ({ item, index }));
  const nonFinancialTypes = ["settlement", "informational"];
  const financialItems = indexedItems.filter(
    ({ item }) => !nonFinancialTypes.includes(item.line_type)
  );
  const nonFinancialItems = indexedItems.filter(({ item }) =>
    nonFinancialTypes.includes(item.line_type)
  );

  return (
    <Box>
      {/* Header row (hidden on mobile) */}
      {!isMdDown && (
        <Grid
          container
          spacing={0.3}
          alignItems="center"
          sx={{
            minHeight: "24px",
            mb: 1,
            width: "100%",
            m: 0,
            "& > .MuiGrid-item": { minWidth: 0 },
          }}
        >
          <Grid item xs={0.5}>
            <Typography variant="subtitle1" sx={{ textAlign: "center" }}>
              #
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="subtitle1">{t("sku")}</Typography>
          </Grid>
          <Grid item xs={3.4}>
            <Typography variant="subtitle1">{t("description")}</Typography>
          </Grid>
          <Grid item xs={1.1}>
            <Typography variant="subtitle1">{t("quantity")}</Typography>
          </Grid>
          <Grid item xs={1.1}>
            <Typography variant="subtitle1">{t("unitPrice")}</Typography>
          </Grid>
          <Grid item xs={0.8}>
            <Typography variant="subtitle1">{t("vatPercentageShort")}</Typography>
          </Grid>
          <Grid item xs={1.1}>
            <Typography variant="subtitle1">{t("lineAmount")}</Typography>
          </Grid>
          <Grid item xs={1}>
            <Typography variant="subtitle1">{t("unitOfMeasure")}</Typography>
          </Grid>
          <Grid item xs={0.5}>
            <Tooltip title={t("distributeLine")}>
              <Typography variant="caption" sx={{ display: "block", textAlign: "center" }}>
                {t("distributeLineShort")}
              </Typography>
            </Tooltip>
          </Grid>
          <Grid item xs={0.5}></Grid>
        </Grid>
      )}

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        {financialItems.map(({ item, index }, position) =>
          isMdDown ? (
            <Box
              key={index}
              sx={{
                mb: 1.5,
                p: 1,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "background.paper",
                ...distributedRowSx(item),
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {t("lineItem")} #{position + 1}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <TextField
                    label={t("sku")}
                    value={item.sku}
                    onChange={(e) => handleLineItemChange(index, "sku", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 13 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("description")}
                    value={item.description}
                    onChange={(e) => handleLineItemChange(index, "description", e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("quantity")}
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleLineItemChange(index, "quantity", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ step: "0.001" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("unitPrice")}
                    type="number"
                    value={item.unit_price}
                    onChange={(e) => handleLineItemChange(index, "unit_price", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ step: "any" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("vatPercentage")}
                    type="number"
                    value={item.vat_percentage ?? ""}
                    onChange={(e) => handleLineItemChange(index, "vat_percentage", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ min: 0, max: 100, step: "0.01" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("lineAmount")}
                    type="number"
                    value={item.total_amount ?? getLineAmount(item).toFixed(2)}
                    onChange={(e) => handleLineItemChange(index, "total_amount", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ step: "0.01" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("unitOfMeasure")}
                    value={item.unit_of_measure}
                    onChange={(e) => handleLineItemChange(index, "unit_of_measure", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 5 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={item.line_type === "distributed"}
                        onChange={(e) => handleDistributeToggle(index, e.target.checked)}
                      />
                    }
                    label={t("distributeLine")}
                  />
                </Grid>
              </Grid>
              <MDBox display="flex" justifyContent="flex-end" mt={1}>
                <MDButton
                  type="button"
                  variant="text"
                  color="error"
                  onClick={() => handleRemoveLineItem(index)}
                >
                  <Icon>delete</Icon>
                </MDButton>
              </MDBox>
            </Box>
          ) : (
            <Grid
              container
              spacing={0.3}
              key={index}
              alignItems="center"
              sx={{
                mb: 0.1,
                width: "100%",
                m: 0,
                "& > .MuiGrid-item": { minWidth: 0 },
                "& .MuiInputBase-root, & input": { minWidth: 0 },
                ...distributedRowSx(item),
              }}
            >
              <Grid item xs={0.5}>
                <Typography variant="body2" sx={{ textAlign: "center" }}>
                  {position + 1}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <TextField
                  value={item.sku}
                  onChange={(e) => handleLineItemChange(index, "sku", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{
                    maxLength: 13,
                    style: { fontSize: "0.75rem", padding: "5px 8px" },
                  }}
                />
              </Grid>
              <Grid item xs={3.4}>
                <Tooltip title={item.description}>
                  <TextField
                    value={item.description}
                    onChange={(e) => handleLineItemChange(index, "description", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{
                      style: { fontSize: "0.75rem", padding: "5px 8px" },
                    }}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={1.1}>
                <TextField
                  type="number"
                  value={item.quantity}
                  onChange={(e) => handleLineItemChange(index, "quantity", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{
                    style: { fontSize: "0.75rem", padding: "5px 6px" },
                    step: "0.001",
                  }}
                />
              </Grid>
              <Grid item xs={1.1}>
                <TextField
                  type="number"
                  value={item.unit_price}
                  onChange={(e) => handleLineItemChange(index, "unit_price", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{
                    style: { fontSize: "0.75rem", padding: "5px 6px" },
                    step: "any",
                  }}
                />
              </Grid>
              <Grid item xs={0.8}>
                <TextField
                  type="number"
                  value={item.vat_percentage ?? ""}
                  onChange={(e) => handleLineItemChange(index, "vat_percentage", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{
                    "aria-label": t("vatPercentage"),
                    min: 0,
                    max: 100,
                    step: "0.01",
                    style: { fontSize: "0.75rem", padding: "5px 4px" },
                  }}
                />
              </Grid>
              <Grid item xs={1.1}>
                <TextField
                  type="number"
                  value={item.total_amount ?? getLineAmount(item).toFixed(2)}
                  onChange={(e) => handleLineItemChange(index, "total_amount", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{
                    style: { fontSize: "0.75rem", padding: "5px 6px" },
                    step: "0.01",
                  }}
                />
              </Grid>
              <Grid item xs={1}>
                <TextField
                  value={item.unit_of_measure}
                  onChange={(e) => handleLineItemChange(index, "unit_of_measure", e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{
                    maxLength: 5,
                    style: { fontSize: "0.75rem", padding: "5px 8px", width: "50px" },
                  }}
                />
              </Grid>
              <Grid item xs={0.5}>
                <Tooltip title={t("distributeLine")}>
                  <MDBox display="flex" justifyContent="center">
                    <Checkbox
                      size="small"
                      sx={{ p: 0.25 }}
                      checked={item.line_type === "distributed"}
                      onChange={(e) => handleDistributeToggle(index, e.target.checked)}
                      inputProps={{ "aria-label": t("distributeLine") }}
                    />
                  </MDBox>
                </Tooltip>
              </Grid>
              <Grid item xs={0.5}>
                <MDBox display="flex" justifyContent="center">
                  <MDButton
                    type="button"
                    variant="text"
                    color="error"
                    sx={{ minWidth: 0, px: 0.5 }}
                    onClick={() => handleRemoveLineItem(index)}
                  >
                    <Icon>delete</Icon>
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          )
        )}

        {nonFinancialItems.length > 0 && (
          <Box
            sx={{
              mt: 3,
              p: { xs: 1.5, sm: 2 },
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              {t("nonFinancialEntries")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("nonFinancialEntriesHint")}
            </Typography>
            {nonFinancialItems.map(({ item, index }) => (
              <Box
                key={item.id ?? index}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr 1fr",
                    sm: "minmax(180px, 2fr) repeat(3, minmax(80px, 1fr)) auto",
                  },
                  gap: 1,
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <TextField
                  label={
                    item.line_type === "settlement" ? t("settlementEntry") : t("informationalEntry")
                  }
                  value={item.description ?? ""}
                  onChange={(e) => handleLineItemChange(index, "description", e.target.value)}
                  size="small"
                  fullWidth
                  sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}
                />
                <TextField
                  label={t("quantity")}
                  type="number"
                  value={item.quantity ?? ""}
                  onChange={(e) => handleLineItemChange(index, "quantity", e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label={t("unitPrice")}
                  type="number"
                  value={item.unit_price ?? ""}
                  onChange={(e) => handleLineItemChange(index, "unit_price", e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label={t("lineAmount")}
                  type="number"
                  value={item.total_amount ?? getLineAmount(item).toFixed(2)}
                  onChange={(e) => handleLineItemChange(index, "total_amount", e.target.value)}
                  size="small"
                  fullWidth
                />
                <MDButton
                  type="button"
                  variant="text"
                  color="error"
                  sx={{ minWidth: 0, px: 0.5, justifySelf: "end" }}
                  onClick={() => handleRemoveLineItem(index)}
                  aria-label={t("remove")}
                >
                  <Icon>delete</Icon>
                </MDButton>
              </Box>
            ))}
          </Box>
        )}

        {/* Add Line Item Button */}
        <Grid container justifyContent="center" spacing={2} sx={{ mt: 2 }}>
          <Grid item>
            <MDButton
              type="button"
              variant="outlined"
              color="info"
              onClick={handleAddLineItem}
              startIcon={<Add />}
            >
              {t("addLineItem")}
            </MDButton>
          </Grid>
          <Grid item>
            <MDButton
              type="submit"
              variant="contained"
              color="success"
              onClick={handleSave}
              startIcon={<Save />}
              disabled={isSaving}
            >
              {isSaving ? t("saving") || "Saving..." : t("saveChanges")}
            </MDButton>
          </Grid>
        </Grid>
      </Box>

      {/* Financial summary below the lines, with room for adjustment descriptions. */}
      <Box
        sx={{
          mt: 3,
          width: "100%",
          maxWidth: 520,
          mx: "auto",
          px: { xs: 1, sm: 2 },
        }}
      >
        <Box display="flex" justifyContent="space-between" gap={2} mb={0.5}>
          <Typography variant="subtitle1">{t("totalQuantity")}:</Typography>
          <Typography variant="subtitle1" sx={{ whiteSpace: "nowrap" }}>
            {totalQuantity}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={0.5}>
          <Typography variant="subtitle1">{t("total")}:</Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            {isLineItemsMismatch && (
              <Tooltip title={t("lineItemsMismatch") || "Eilučių suma nesutampa su suma be PVM"}>
                <WarningAmber color="warning" sx={{ fontSize: "1.2rem" }} />
              </Tooltip>
            )}
            <Typography
              variant="subtitle1"
              sx={{
                whiteSpace: "nowrap",
                fontVariantNumeric: "tabular-nums",
                color: isLineItemsMismatch ? "warning.main" : "text.primary",
                fontWeight: isLineItemsMismatch ? 700 : 400,
              }}
            >
              {totalAmount.toFixed(2)}
            </Typography>
          </Box>
        </Box>
        {adjustments.map((adjustment, index) => (
          <Box
            key={`adjustment-${index}`}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            mb={0.5}
            sx={adjustment.disabled ? { opacity: 0.5 } : undefined}
          >
            <Box display="flex" alignItems="center" sx={{ minWidth: 0 }}>
              <Tooltip title={t("applyAdjustmentHint")}>
                <Checkbox
                  size="small"
                  sx={{ p: 0.25, mr: 0.5 }}
                  checked={!adjustment.disabled}
                  onChange={(e) => handleAdjustmentToggle(index, e.target.checked)}
                  inputProps={{ "aria-label": t("applyAdjustmentHint") }}
                />
              </Tooltip>
              <Typography variant="subtitle1" sx={{ minWidth: 0 }}>
                {adjustment.description || adjustment.type || t("adjustment")}:
              </Typography>
            </Box>
            <Typography
              variant="subtitle1"
              sx={{
                whiteSpace: "nowrap",
                fontVariantNumeric: "tabular-nums",
                textDecoration: adjustment.disabled ? "line-through" : "none",
              }}
            >
              {Number(parseNumber(adjustment.amount) || 0).toFixed(2)}
            </Typography>
          </Box>
        ))}
        {adjustments.length > 0 && (
          <Box
            display="flex"
            justifyContent="space-between"
            gap={2}
            pt={1}
            mt={1}
            borderTop={1}
            borderColor="divider"
          >
            <Typography variant="subtitle1" fontWeight={700}>
              {t("netAmount")}:
            </Typography>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
            >
              {adjustedTotal.toFixed(2)}
            </Typography>
          </Box>
        )}
        {adjustments.length === 0 && validatedDiscountAmount > 0 && (
          <Box display="flex" justifyContent="space-between" gap={2} mt={1}>
            <Typography variant="subtitle1">
              {t("discount")} ({validatedDiscountPercentage.toFixed(2)}%):
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
            >
              {validatedDiscountAmount.toFixed(2)}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

InvoiceLineItems.propTypes = {
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      sku: PropTypes.string,
      description: PropTypes.string,
      quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      unit_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      total_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      vat_percentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      unit_of_measure: PropTypes.string,
      line_type: PropTypes.string,
    })
  ),
  onSave: PropTypes.func.isRequired,
  onDirtyChange: PropTypes.func,
  onItemsChange: PropTypes.func,
  isSaving: PropTypes.bool,
  discountAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  discountPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  financialAdjustments: PropTypes.arrayOf(PropTypes.object),
  onAdjustmentsChange: PropTypes.func,
  netAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default InvoiceLineItems;
